import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useTagStore from '../../store/tagStore';

// Common color palette
const COLOR_PALETTE = [
  '#FFD700', // Gold
  '#87CEEB', // Sky Blue
  '#FFB6C1', // Light Pink
  '#98FB98', // Pale Green
  '#DDA0DD', // Plum
  '#F0E68C', // Khaki
  '#FFE4B5', // Moccasin
  '#E0BBE4', // Lavender
  '#FFDAB9', // Peach
  '#B0E0E6', // Powder Blue
  '#FFE4E1', // Misty Rose
  '#F5DEB3', // Wheat
];

// Common emojis for tags
const EMOJI_OPTIONS = [
  // Proteins/Meats
  '�', // Beef
  '🍗', // Chicken
  '🦆', // Duck
  '🦐', // Shrimp
  '🥚', // Egg
  '🐟', // Fish
  '🍖', // Meat on bone
  '�', // Bacon
  '🍤', // Fried shrimp
  // Vegetables & Produce
  '🥗', '�🥦', '🥕', '🌽', '🍅', '🥒', '�️', '🫑',
  // Meals & Dishes
  '�🍜', '🍲', '🥘', '🍱', '🍛', '🥙', '🌮', '🍕',
  // Beverages
  '☕', '🥤', '🍵', '🥛', '🧃', '🧋',
  // Desserts
  '🍰', '🍪', '🍩', '🧁', '🍨', '🍮',
];

const TagDialog = ({ open, tag, onClose }) => {
  const { t } = useTranslation();
  const { createTag, updateTag } = useTagStore();
  const isEditing = !!tag;

  const [formData, setFormData] = useState({
    name: '',
    color: '#FFD700',
    icon: '🏷️'
  });

  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name,
        color: tag.color,
        icon: tag.icon
      });
    } else {
      setFormData({
        name: '',
        color: '#FFD700',
        icon: '🏷️'
      });
    }
  }, [tag]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      if (isEditing) {
        await updateTag(tag.id, formData);
      } else {
        await createTag(formData);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save tag:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing
          ? t('tags.editTag', 'Edit Tag')
          : t('tags.createTag', 'Create New Tag')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            label={t('tags.name', 'Tag Name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" gutterBottom>
            {t('tags.selectColor', 'Select Color')}
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {COLOR_PALETTE.map((color) => (
              <Grid item key={color}>
                <Box
                  onClick={() => handleChange('color', color)}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: formData.color === color ? '3px solid #000' : '1px solid #ddd',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" gutterBottom>
            {t('tags.selectIcon', 'Select Icon (Emoji)')}
          </Typography>
          <Grid container spacing={1}>
            {EMOJI_OPTIONS.map((emoji) => (
              <Grid item key={emoji}>
                <Box
                  onClick={() => handleChange('icon', emoji)}
                  sx={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: formData.icon === emoji ? '3px solid #000' : '1px solid #ddd',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {emoji}
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, p: 2, backgroundColor: formData.color, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('tags.preview', 'Preview')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '1.5rem' }}>{formData.icon}</span>
              <Typography variant="body1" fontWeight={500}>
                {formData.name || t('tags.tagName', 'Tag Name')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.buttons.cancel', 'Cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!formData.name.trim()}
        >
          {t('common.buttons.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagDialog;
